import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Zap,
  AlertTriangle,
  Lock,
  ChevronRight,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { formatBRL, formatPercent, formatDate, formatDateTime, cn } from '../lib/utils.js';

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function previousMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function nextMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 7);
}

const categoryColor: Record<string, string> = {
  'Tráfego Pago': 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  'IA / LLM': 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  'Infra SaaS': 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  'Comunicação': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Outros: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

export function Financeiro(): JSX.Element {
  const [month, setMonth] = useState<string>(currentMonth());

  const overviewQ = useQuery({
    queryKey: ['finance-overview', month],
    queryFn: () => apiClient.financeOverview(month),
  });

  const entriesQ = useQuery({
    queryKey: ['finance-journal-entries', 20],
    queryFn: () => apiClient.journalEntries({ limit: 20 }),
  });

  const costsQ = useQuery({
    queryKey: ['finance-cost-events', month],
    queryFn: () => apiClient.costEvents({ month, limit: 30 }),
  });

  if (overviewQ.isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando financeiro...</div>;
  }

  if (overviewQ.isError) {
    return (
      <div className="p-8 text-destructive">
        Erro ao carregar: {String(overviewQ.error)}
      </div>
    );
  }

  const data = overviewQ.data;
  if (!data) return <div className="p-8">Sem dados</div>;

  const { kpis, revenueByProduct, costsByCategory, dailyRevenue } = data;
  const totalCosts = Number(kpis.totalCostsCents);
  const maxCategoryAmount = costsByCategory.length
    ? Math.max(...costsByCategory.map((c) => Number(c.amountCents)))
    : 0;
  const maxDailyRevenue = dailyRevenue.length
    ? Math.max(...dailyRevenue.map((d) => Number(d.grossCents)))
    : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Financeiro</h1>
          <p className="text-sm text-muted-foreground">
            DRE · Custos · Ledger Contábil · Shadow Ledger (PF)
          </p>
        </div>

        <div className="flex items-center gap-2 bg-muted/40 rounded-md px-1 py-1 border border-border">
          <button
            onClick={() => setMonth(previousMonth(month))}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            ←
          </button>
          <div className="px-3 py-1 text-sm font-medium tabular-nums">{month}</div>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={month >= currentMonth()}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            →
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Receita Bruta</span>
              <DollarSign className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-xl font-bold tabular-nums">
              {formatBRL(kpis.grossRevenueCents)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {kpis.approvedOrders} pedidos aprovados
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Receita Líquida</span>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="text-xl font-bold tabular-nums">
              {formatBRL(kpis.netRevenueCents)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Taxa gateway: {formatBRL(kpis.gatewayFeesCents)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Custos Totais</span>
              <TrendingDown className="h-4 w-4 text-rose-500" />
            </div>
            <div className="text-xl font-bold tabular-nums">{formatBRL(totalCosts)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Ad spend: {formatBRL(kpis.adSpendCents)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Lucro Bruto</span>
              <Zap
                className={cn(
                  'h-4 w-4',
                  kpis.grossProfitCents >= 0 ? 'text-emerald-500' : 'text-rose-500',
                )}
              />
            </div>
            <div
              className={cn(
                'text-xl font-bold tabular-nums',
                kpis.grossProfitCents >= 0 ? 'text-emerald-400' : 'text-rose-400',
              )}
            >
              {formatBRL(kpis.grossProfitCents)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Margem {formatPercent(kpis.margin)} · ROAS {kpis.roas.toFixed(2)}x
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alertas */}
      {kpis.grossProfitCents < 0 && (
        <div className="flex items-start gap-3 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-rose-300">
              Operação no vermelho neste mês (dados demo)
            </div>
            <div className="text-rose-200/70">
              ROAS atual {kpis.roas.toFixed(2)}x — abaixo do breakeven contábil. Em produção,
              priorizar criativos com CTR &gt;1.8% e pausar conjuntos &lt;1.0x.
            </div>
          </div>
        </div>
      )}

      {/* Receita por produto + Custos por categoria */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Receita por Produto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {revenueByProduct.length === 0 && (
              <div className="text-sm text-muted-foreground">Sem vendas neste mês.</div>
            )}
            {revenueByProduct.map((p) => {
              const gross = Number(p.grossCents);
              const max = Math.max(...revenueByProduct.map((x) => Number(x.grossCents)));
              const pct = max ? (gross / max) * 100 : 0;
              return (
                <div key={`${p.product}-${p.kind}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.product}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {p.kind}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <div className="tabular-nums font-medium">{formatBRL(gross)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {p.orders} pedido{p.orders === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500/70 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Custos por Categoria</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {costsByCategory.length === 0 && (
              <div className="text-sm text-muted-foreground">Sem custos neste mês.</div>
            )}
            {costsByCategory.map((c) => {
              const amt = Number(c.amountCents);
              const pct = maxCategoryAmount ? (amt / maxCategoryAmount) * 100 : 0;
              const share = totalCosts ? (amt / totalCosts) * 100 : 0;
              return (
                <div key={c.category}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn('text-[10px]', categoryColor[c.category])}
                      >
                        {c.category}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {c.events} eventos
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="tabular-nums font-medium">{formatBRL(amt)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {share.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
                    <div
                      className="h-full bg-rose-500/70 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Sparkline receita diária */}
      {dailyRevenue.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Receita diária (bruta) — {month}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {dailyRevenue.map((d) => {
                const v = Number(d.grossCents);
                const h = maxDailyRevenue ? (v / maxDailyRevenue) * 100 : 0;
                return (
                  <div
                    key={d.day}
                    className="flex-1 flex flex-col items-center justify-end gap-1 group"
                    title={`${formatDate(d.day)} — ${formatBRL(v)} (${d.orders} pedidos)`}
                  >
                    <div
                      className="w-full bg-emerald-500/60 rounded-sm group-hover:bg-emerald-400 transition-colors"
                      style={{ height: `${Math.max(h, 4)}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
              <span>{formatDate(dailyRevenue[0]?.day)}</span>
              <span>{formatDate(dailyRevenue[dailyRevenue.length - 1]?.day)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost events + Journal entries */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Cost Events recentes ({month})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card border-b border-border">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Data</th>
                    <th className="px-3 py-2 font-medium">Categoria</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 font-medium text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(costsQ.data?.events ?? []).map((e) => (
                    <tr
                      key={e.id}
                      className="border-b border-border/40 hover:bg-muted/30"
                    >
                      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">
                        {formatDate(e.occurredAt)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          variant="outline"
                          className={cn('text-[10px]', categoryColor[e.category])}
                        >
                          {e.category}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[160px]">
                        {e.source}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatBRL(e.amountCents)}
                      </td>
                    </tr>
                  ))}
                  {!costsQ.data?.events.length && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                        Sem cost events neste mês.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Journal Entries (Ledger Dupla Entrada)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-y-auto divide-y divide-border/40">
              {(entriesQ.data?.entries ?? []).map((je) => (
                <Link
                  key={je.id}
                  to={`/financeiro/ledger/${je.id}`}
                  className="block px-4 py-3 hover:bg-muted/30"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {formatDate(je.competencia)}
                        </span>
                        {je.lockedAt && (
                          <Lock className="h-3 w-3 text-emerald-500" />
                        )}
                        <Badge variant="outline" className="text-[10px]">
                          {je.sourceType}
                        </Badge>
                      </div>
                      <div className="text-sm font-medium truncate">
                        {je.description}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {je.lineCount} linhas
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="tabular-nums text-sm font-medium">
                        {formatBRL(je.totalDebitCents)}
                      </div>
                      <ChevronRight className="h-3 w-3 text-muted-foreground ml-auto mt-0.5" />
                    </div>
                  </div>
                </Link>
              ))}
              {!entriesQ.data?.entries.length && (
                <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                  Sem journal entries.
                </div>
              )}
            </div>
            {entriesQ.data && entriesQ.data.total > entriesQ.data.entries.length && (
              <div className="px-4 py-2 text-[10px] text-muted-foreground border-t border-border">
                Mostrando {entriesQ.data.entries.length} de {entriesQ.data.total} entries
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer info */}
      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <strong>Shadow Ledger (PF):</strong> Todos os lançamentos são gerenciais. CNPJ ainda
        não emitido — fase Caminho 3 (até 17/11/2026). Carnê-Leão IRPF mensal provisionado.
        Última atualização: {overviewQ.dataUpdatedAt ? formatDateTime(new Date(overviewQ.dataUpdatedAt).toISOString()) : '—'}.
      </div>
    </div>
  );
}
